import type { AccessPermission, AccessRole, AccessState } from "@/lib/api/accessStatus";

/**
 * Helpers for GET /admin/access-status, shared by the Tenant connection page
 * and the module cards so the two never count the same rows differently.
 *
 * Three states and no fourth. `unknown` is what the probe could not measure —
 * an unverified credential, a capability gated behind a module that is off —
 * and it is drawn hatched like every other unmeasured value in this console. It
 * is never folded into "missing" (which would invent a finding) and never into
 * "granted" (which would hide one).
 */

export interface AccessTally {
  granted: number;
  missing: number;
  unknown: number;
  total: number;
}

export function tallyAccess(rows: ReadonlyArray<{ state: AccessState }>): AccessTally {
  const t: AccessTally = { granted: 0, missing: 0, unknown: 0, total: rows.length };
  for (const r of rows) {
    if (r.state === "granted") t.granted += 1;
    else if (r.state === "missing") t.missing += 1;
    else t.unknown += 1;
  }
  return t;
}

/** "3 permissions missing · 1 unknown", or "All 12 granted"; "" for an empty list. */
export function accessSummary(rows: readonly AccessPermission[]): string {
  const t = tallyAccess(rows);
  if (t.total === 0) return "";
  if (t.missing === 0 && t.unknown === 0) {
    return `All ${t.total} permission${t.total === 1 ? "" : "s"} granted`;
  }
  const parts: string[] = [];
  if (t.missing > 0) parts.push(`${t.missing} permission${t.missing === 1 ? "" : "s"} missing`);
  if (t.unknown > 0) parts.push(`${t.unknown} unknown`);
  return parts.join(" · ");
}

/** The manual-only role rows: shown as instructions, never as done. */
export function manualRoles(rows: readonly AccessRole[]): AccessRole[] {
  return rows.filter((r) => r.manual);
}
